import React, { useRef } from 'react';
import { KPICard } from '../ui';
import { useHRTeamComposition } from '../../hooks/useHRQueries';
import LoadingSpinner from '../ui/LoadingSpinner';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface TeamCompositionProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const TeamComposition: React.FC<TeamCompositionProps> = ({ 
  branchId,
  showLoading = true,
  onError 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useHRTeamComposition(branchId);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'team-composition';
  const componentTitle = 'ترکیب تیم';

  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <LoadingSpinner variant="minimal" size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base border border-status-error p-4">
        <p className="text-status-error">خطا در بارگذاری ترکیب تیم</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="kpi"
          dashboardSource="hr"
          onInfo={() => openGuide(CHART_GUIDES.teamComposition)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'hr',
            componentType: 'kpi',
            description: 'ترکیب تیم پرسنل بر اساس نقش',
            props: { branchId, showLoading, onError },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
          className="mb-4"
        />
        <h3 className="title-card">{componentTitle}</h3>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        <KPICard
          title="صندوقدار"
          value={data.cashiers}
          variant="info"
          accentStyle="corner"
          className="p-2 min-h-0 shadow-sm hover:shadow-md"
          aria-label={`تعداد صندوقداران: ${data.cashiers}`}
        />
        
        <KPICard
          title="پیک"
          value={data.couriers}
          variant="warning"
          accentStyle="corner"
          className="p-2 min-h-0 shadow-sm hover:shadow-md"
          aria-label={`تعداد پیک‌ها: ${data.couriers}`}
        />
        
        <KPICard
          title="میزبان"
          value={data.hosts}
          variant="primary"
          accentStyle="corner"
          className="p-2 min-h-0 shadow-sm hover:shadow-md"
          aria-label={`تعداد میزبان‌ها: ${data.hosts}`}
        />
      </div>
    </div>
  );
};

export default TeamComposition;